import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import GroupRegistrationForm from './components/GroupRegistrationForm';
import IndividualRegistrationForm from './components/IndividualRegistrationForm';
import CostumeRegistrationForm from './components/CostumeRegistrationForm';
import ArtistRegistrationForm from './components/ArtistRegistrationForm';
import PublicGroupsMap from './components/PublicGroupsMap';
import PublicIndividualsMap from './components/PublicIndividualsMap';
import PublicCostumeMakersPage from './components/PublicCostumeMakersPage';
import PublicArtistsPage from './components/PublicArtistsPage';
import AdminDashboard from './components/AdminDashboard';
import UserDashboard from './components/UserDashboard';
import Login from './components/Login';
import Register from './components/Register';
import EventRegistrationForm from './components/EventRegistrationForm';
import EventsPage from './components/EventsPage';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';

function NavBar({ user, onLogout }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setMenuOpen(false); }, [location]);
  return (
    <nav className="navbar">
      <div className="nav-container">
        <Link to="/" className="nav-brand">
          <img src="/Krampus_America_Logo.png" alt="Krampus America" className="nav-logo" />
        </Link>
        <button
          className="nav-hamburger"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span></span><span></span><span></span>
        </button>
        <div className={`nav-links ${menuOpen ? 'nav-open' : ''}`}>
          <Link to="/groups">Groups</Link>
          <Link to="/performers">Performers</Link>
          <Link to="/costumers">Costumers</Link>
          <Link to="/artists">Krampus Artists</Link>
          <Link to="/events">Events</Link>
          {user ? (
            <>
              <Link to="/dashboard">My Dashboard</Link>
              {user.role === 'admin' && <Link to="/admin">Admin</Link>}
              <button onClick={onLogout} className="logout-btn">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login">Login</Link>
              <Link to="/signup">Sign Up</Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      try { setUser(JSON.parse(userData)); }
      catch (e) { localStorage.removeItem('token'); localStorage.removeItem('user'); }
    }
    setLoading(false);
  }, []);
  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    window.location.href = '/';
  };
  if (loading) return <div className="loading">Loading...</div>;
  return (
    <Router>
      <div className="App">
        <NavBar user={user} onLogout={handleLogout} />
        <Routes>
          <Route path="/" element={<Home user={user} />} />
          <Route path="/groups" element={<PublicGroupsMap />} />
          <Route path="/performers" element={<PublicIndividualsMap />} />
          <Route path="/costumers" element={<PublicCostumeMakersPage />} />
          <Route path="/artists" element={<PublicArtistsPage />} />
          <Route path="/events" element={<EventsPage user={user} />} />
          <Route path="/register-group" element={user ? <GroupRegistrationForm user={user} /> : <Navigate to="/login" />} />
          <Route path="/register-performer" element={user ? <IndividualRegistrationForm user={user} /> : <Navigate to="/login" />} />
          <Route path="/register-costume" element={user ? <CostumeRegistrationForm user={user} /> : <Navigate to="/login" />} />
          <Route path="/register-artist" element={user ? <ArtistRegistrationForm user={user} /> : <Navigate to="/login" />} />
          <Route path="/register-event" element={user ? <EventRegistrationForm user={user} /> : <Navigate to="/login" />} />
          <Route path="/dashboard" element={user ? <UserDashboard user={user} /> : <Navigate to="/login" />} />
          <Route path="/admin" element={user?.role === 'admin' ? <AdminDashboard user={user} /> : <Navigate to="/" />} />
          <Route path="/login" element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />} />
          <Route path="/signup" element={!user ? <Register onLogin={handleLogin} /> : <Navigate to="/" />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Routes>
      </div>
    </Router>
  );
}

function Home({ user }) {
  return (
    <div className="home">
      <div className="hero">
        <img src="/Krampus_America_Logo.png" alt="Krampus America" className="hero-logo" />
        <h1>Welcome to Krampus America</h1>
        <p>The official registry of Krampus groups, performers, costume makers, artists, and events across the United States and Canada</p>
        <div className="cta-buttons">
          <Link to="/register-group" className="btn btn-primary">Register a Group</Link>
          <Link to="/register-performer" className="btn btn-secondary">Register as Performer</Link>
          <Link to="/register-costume" className="btn btn-secondary">Register as Costume Maker</Link>
          <Link to="/register-artist" className="btn btn-secondary">Register as Artist</Link>
          <Link to="/register-event" className="btn btn-secondary">Submit an Event</Link>
        </div>
      </div>
      <div className="info-section">
        <h2>Explore the Krampus Community</h2>
        <p>Find registered Krampus groups, performers, costume makers, and artists across the United States and Canada</p>
        <div className="map-links">
          <Link to="/groups" className="btn btn-secondary">View Groups</Link>
          <Link to="/performers" className="btn btn-secondary">View Performers</Link>
          <Link to="/costumers" className="btn btn-secondary">View Costumers</Link>
          <Link to="/artists" className="btn btn-secondary">View Artists</Link>
          <Link to="/events" className="btn btn-secondary">View Events</Link>
        </div>
      </div>
      <div className="info-section">
        <h2>About Registration</h2>
        <p>All registrations are reviewed by our administrators before being published. Please ensure your information is publication-ready and accurate.</p>
        <ul>
          <li>Create an account to submit your registration</li>
          <li>Upload photos — up to 3 for groups/performers/events, 6 for costume makers, 10 for artists</li>
          <li>All submissions require approval before going live</li>
          <li>You can update your information anytime (updates also require approval)</li>
          <li>Check your dashboard for status updates</li>
        </ul>
      </div>
    </div>
  );
}

export default App;
